import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi, UpdateProfileData } from '@/lib/api';
import { useAuth } from '@/hooks/use-auth';
import type { User } from '@/types';

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const { user, refreshUser } = useAuth();

  return useMutation({
    mutationFn: async (data: UpdateProfileData) => {
      if (!user?.id) throw new Error('Not authenticated');
      const response = await usersApi.update(user.id, data);
      return response.data as User;
    },
    onSuccess: () => {
      refreshUser();
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async ({
      currentPassword,
      newPassword,
    }: {
      currentPassword: string;
      newPassword: string;
    }) => {
      const response = await usersApi.changePassword(currentPassword, newPassword);
      return response.data as { success: boolean };
    },
  });
}

export function useUploadMyAvatar() {
  const queryClient = useQueryClient();
  const { refreshUser } = useAuth();

  return useMutation({
    mutationFn: async (file: File) => {
      const response = await usersApi.uploadMyAvatar(file);
      return response.data as User;
    },
    onSuccess: async () => {
      await refreshUser();
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useRemoveMyAvatar() {
  const queryClient = useQueryClient();
  const { refreshUser } = useAuth();

  return useMutation({
    mutationFn: async () => {
      const response = await usersApi.removeMyAvatar();
      return response.data as User;
    },
    onSuccess: async () => {
      await refreshUser();
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}
